import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  currentPageSelector,
  videoSearchInputSelector,
  videoUploadSuccessSelector,
} from '@assetManagement/selectors/uploadVideo';
import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import {
  setVideoUploadSuccess,
  setCurrentPage,
} from '@assetManagement/store/uploadVideo';
import { useGetUploadedVideosQuery } from '@assetManagement/services/uploadVideoService';
import UploadedVideosPagination from '../UploadedVideosPagination';
import UploadedVideosTable from '../UploadedVideosTable';
import useSearchDebouncedPromise from '@assetManagement/hooks/useSearchDebouncedPromise';

const MIN_KEYWORD_INPUT_LENGTH = 1;
const MIN_KEYWORD_LENGTH = 3;
const MAX_VIDEOS_LIMIT = 20;

export default function UploadedVideos() {
  const dispatch = useDispatch();
  const currentPage = useSelector(currentPageSelector);
  const videoSearchInput = useSelector(videoSearchInputSelector);
  const videoUploadSuccess = useSelector(videoUploadSuccessSelector);
  const debouncedSearchQuery = useSearchDebouncedPromise(
    videoSearchInput,
    DEBOUNCE_DURATION
  );

  const { refetch, isFetching, error, data } = useGetUploadedVideosQuery(
    { searchQuery: debouncedSearchQuery, currentPage },
    {
      skip:
        debouncedSearchQuery.length >= MIN_KEYWORD_INPUT_LENGTH &&
        debouncedSearchQuery.length < MIN_KEYWORD_LENGTH,
    }
  );

  useEffect(() => {
    if (videoUploadSuccess) {
      dispatch(setCurrentPage(1));
      refetch({ currentPage: 1 });
      dispatch(setVideoUploadSuccess(false));
    }
  }, [dispatch, refetch, videoUploadSuccess]);

  const calculatePageCount = useMemo(() => {
    const totalCount = data?.meta?.total_count || 0;
    return Math.ceil(totalCount / MAX_VIDEOS_LIMIT);
  }, [data]);

  return (
    <>
      <h3>Videos History</h3>
      <UploadedVideosTable
        uploadedVideos={data?.data}
        isLoading={isFetching}
        error={error}
      />
      <UploadedVideosPagination
        pageCount={calculatePageCount}
        currentPage={currentPage}
      />
    </>
  );
}
